import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';

const VersionSection = () => {
    const [activeTab, setActiveTab] = useState('makes'); // 'makes' | 'models' | 'powers' | 'colors'
    const [listData, setListData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Form input state
    const [inputValue, setInputValue] = useState('');
    
    // Editing state
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');

    // Fetch data based on selected tab
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get(`/v1/specs/${activeTab}`);
            if (res.data.success) {
                setListData(res.data.data || []);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        setInputValue('');
        setEditingId(null);
        fetchData();
    }, [activeTab, fetchData]);

    // Add Record
    const handleAdd = async (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;

        const payload = activeTab === 'powers' ? { cc: inputValue } : { name: inputValue };

        try {
            const res = await API.post(`/v1/specs/${activeTab}`, payload);
            if (res.data.success) {
                setInputValue('');
                fetchData();
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to add item');
        }
    };

    // Start Editing
    const startEditing = (item) => {
        setEditingId(item.id);
        setEditValue(activeTab === 'powers' ? item.cc : item.name);
    };

    // Save Update
    const handleUpdate = async (id) => {
        if (!editValue.trim()) return;
        const payload = activeTab === 'powers' ? { cc: editValue } : { name: editValue };

        try {
            const res = await API.put(`/v1/specs/${activeTab}/${id}`, payload);
            if (res.data.success) {
                setEditingId(null);
                fetchData();
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update item');
        }
    };

    // Delete Record
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this record?')) {
            try {
                const res = await API.delete(`/v1/specs/${activeTab}/${id}`);
                if (res.data.success) {
                    fetchData();
                }
            } catch (err) {
                alert(err.response?.data?.message || 'Failed to delete item');
            }
        }
    };

    // Active Tab Tab Labels & Placeholders
    const tabConfigs = {
        makes: { title: 'Make / Brand', label: 'Make Name', placeholder: 'e.g. Honda, Yamaha, Suzuki' },
        models: { title: 'Bike Model', label: 'Model Name', placeholder: 'e.g. CD-70, CG-125, YBR' },
        powers: { title: 'Power (CC)', label: 'Displacement (CC)', placeholder: 'e.g. 70cc, 125cc, 150cc' },
        colors: { title: 'Color', label: 'Color Name', placeholder: 'e.g. Red, Black, Metallic Blue' },
    };

    return (
        <div style={styles.container}>
            <style>{`
                .spec-tab-btn { transition: all 0.2s ease; }
                .spec-tab-btn:hover { background-color: #f1f5f9; }
                .spec-row:hover { background-color: #f8fafc; }
                .spec-input:focus { border-color: #10b981 !important; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }
                .action-btn { transition: 0.2s; cursor: pointer; border: none; border-radius: 6px; padding: 6px 12px; font-weight: 600; font-size: 12px; }
                .btn-edit { background: #e0f2fe; color: #0284c7; margin-right: 6px; }
                .btn-edit:hover { background: #bae6fd; }
                .btn-delete { background: #fee2e2; color: #dc2626; }
                .btn-delete:hover { background: #fecaca; }
                .btn-save { background: #dcfce7; color: #166534; margin-right: 6px; }
                .btn-cancel { background: #f1f5f9; color: #475569; }
            `}</style>

            {/* Main Spec Card */}
            <div style={styles.card}>
                {/* Header Sub-Tabs */}
                <div style={styles.tabsHeader}>
                    {[
                        { id: 'makes', icon: '🏢', name: 'Make' },
                        { id: 'models', icon: '🏍️', name: 'Model' },
                        { id: 'powers', icon: '⚡', name: 'Power (CC)' },
                        { id: 'colors', icon: '🎨', name: 'Color' }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            className="spec-tab-btn"
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                ...styles.tabBtn,
                                borderBottom: activeTab === tab.id ? '3px solid #10b981' : '3px solid transparent',
                                color: activeTab === tab.id ? '#10b981' : '#64748b',
                                fontWeight: activeTab === tab.id ? '700' : '500'
                            }}
                        >
                            <span style={{ marginRight: '8px' }}>{tab.icon}</span>
                            {tab.name}
                        </button>
                    ))}
                </div>

                {/* Sub-Header Banner */}
                <div style={styles.banner}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>
                            {tabConfigs[activeTab].title} Management
                        </h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                            Add, update or delete {tabConfigs[activeTab].title.toLowerCase()} entries.
                        </p>
                    </div>
                    <div style={styles.countBadge}>
                        {listData.length} <span style={{ fontSize: '10px', display: 'block' }}>TOTAL</span>
                    </div>
                </div>

                {/* Add Item Form */}
                <form onSubmit={handleAdd} style={styles.formArea}>
                    <div style={{ flex: 1 }}>
                        <label style={styles.label}>{tabConfigs[activeTab].label}</label>
                        <input
                            className="spec-input"
                            style={styles.input}
                            placeholder={tabConfigs[activeTab].placeholder}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                    </div>
                    <button type="submit" style={styles.addBtn}>
                        + Add {tabConfigs[activeTab].title}
                    </button>
                </form>

                {/* Data Table */}
                <table style={styles.table}>
                    <thead style={styles.thead}>
                        <tr>
                            <th style={{ ...styles.th, width: '60px' }}>#</th>
                            <th style={styles.th}>Name / Value</th>
                            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="3" style={styles.loadingTd}>Loading data...</td>
                            </tr>
                        ) : listData.length === 0 ? (
                            <tr>
                                <td colSpan="3" style={styles.emptyTd}>
                                    No records found in {tabConfigs[activeTab].title}.
                                </td>
                            </tr>
                        ) : (
                            listData.map((item, index) => {
                                const isEditing = editingId === item.id;
                                const displayVal = activeTab === 'powers' ? item.cc : item.name;

                                return (
                                    <tr key={item.id} className="spec-row" style={styles.tr}>
                                        <td style={{ ...styles.td, color: '#94a3b8', fontWeight: 'bold' }}>
                                            {index + 1}
                                        </td>
                                        <td style={styles.td}>
                                            {isEditing ? (
                                                <input
                                                    className="spec-input"
                                                    style={{ ...styles.input, padding: '6px 10px' }}
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                />
                                            ) : (
                                                <span style={{ fontWeight: '600', color: '#1e293b' }}>
                                                    {displayVal}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>
                                            {isEditing ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="action-btn btn-save"
                                                        onClick={() => handleUpdate(item.id)}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn btn-cancel"
                                                        onClick={() => setEditingId(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="action-btn btn-edit"
                                                        onClick={() => startEditing(item)}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn btn-delete"
                                                        onClick={() => handleDelete(item.id)}
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const styles = {
    container: { width: '100%', boxSizing: 'border-box' },
    card: { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    tabsHeader: { display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#ffffff' },
    tabBtn: { flex: 1, padding: '14px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', outline: 'none' },
    banner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'linear-gradient(135deg, #0f172a, #1e293b)', color: '#fff' },
    countBadge: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', padding: '6px 14px', fontSize: '18px', fontWeight: 900, textAlign: 'center' },
    formArea: { display: 'flex', gap: '14px', alignItems: 'flex-end', padding: '20px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    label: { display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' },
    input: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', boxSizing: 'border-box', background: '#fff' },
    addBtn: { padding: '11px 22px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    th: { padding: '14px 18px', textAlign: 'left', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '14px 18px', fontSize: '13px' },
    loadingTd: { padding: '30px', textAlign: 'center', color: '#64748b' },
    emptyTd: { padding: '30px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }
};

export default VersionSection;